import React, { useCallback, useContext } from "react";
import { InternalRefetchQueriesInclude } from "@apollo/client";

import { emitAddToCart, getSourcePlatform } from "@catchoftheday/analytics";
import { Button, ButtonProps } from "@catchoftheday/cg-components";
import { CustomerContext } from "@catchoftheday/customer";

import { AddToCartInput } from "../../generated/graphql";
import { MutationType, useToast } from "../../hooks";

type PersonalisedValue = {
  name: string;
  value: string;
};

type AddToCartButtonProps = {
  offerId: string;
  quantity: number;
  imageUrl: string;
  title: string;
  setError?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  sourceId?: string;
  sourceType?: number;
  sourcePosition?: number;
  eventId?: number;
  onClick?: () => void;
  /** No toast to show for adding to cart. */
  noToast?: boolean;
  fetchPolicy?: "no-cache";
  isFormValid?: () => Promise<boolean>;
  getValues?: () => Record<string, string | number>;
  refetchQueries?: InternalRefetchQueriesInclude;
  awaitRefetchQueries?: boolean;
} & ButtonProps;

export const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  offerId,
  quantity,
  imageUrl,
  title,
  setError,
  setLoading,
  children,
  sourceId,
  sourceType,
  sourcePosition,
  eventId,
  onClick,
  noToast,
  isFormValid,
  getValues,
  refetchQueries,
  awaitRefetchQueries,
  ...props
}) => {
  const { data: customer } = useContext(CustomerContext);

  const addToCart = useToast<AddToCartInput>({
    mutationType: MutationType.ADD_TO_CART,
    setError,
    setLoading: (loading) => {
      setLoading?.(loading);
    },
    noToast,
    onCompleted: onClick,
    refetchQueries,
    awaitRefetchQueries,
  });

  const onAddToCart = useCallback(
    async (personalisedValues?: PersonalisedValue[]) => {
      const { status } = await addToCart(
        { imageUrl, title },
        {
          eventId,
          offerId,
          quantity,
          sourcePlatform: getSourcePlatform(),
          sourceId,
          sourceType: sourceType?.toString(),
          sourcePosition,
          personalisedValues,
        }
      );
      if (status !== "ServerError") {
        emitAddToCart({
          customer_id: customer?.id,
          success: status == "OK",
          source_id: sourceId,
          source_type: sourceType,
          source_position: sourcePosition,
          offer_id: offerId,
          event_id: eventId,
        });
      }
    },
    [
      addToCart,
      imageUrl,
      title,
      offerId,
      quantity,
      customer?.id,
      sourceId,
      sourceType,
      sourcePosition,
      eventId,
    ]
  );

  const onAddPersonalisedProductToCart = useCallback(async () => {
    if (
      (typeof isFormValid == "function" && !(await isFormValid())) ||
      !getValues
    ) {
      return;
    }
    const formData = getValues();
    const personalisedValues: PersonalisedValue[] = Object.keys(formData).map(
      (key) => ({
        name: key,
        value: `${formData[key]}`,
      })
    );
    onAddToCart(personalisedValues);
  }, [onAddToCart, isFormValid, getValues]);

  return (
    <Button
      variant={props.variant ?? "plain"}
      sizeVariant="sm"
      p={0}
      onClick={() => {
        props.type == "submit"
          ? onAddPersonalisedProductToCart()
          : onAddToCart();
      }}
      aria-label="Add to cart"
      color="secondary"
      textTransform="initial"
      fontSize={{ xs: "xs", md: "base" }}
      fontWeight="normal"
      underlineOnHover
      {...props}
    >
      {children}
    </Button>
  );
};
